export interface GalleryPhoto {
  image?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
  source_page_urls?: string | null;
  original_url?: string | null;
}

/** Category titles that are not real marketing photos. */
const EXCLUDED_CATEGORY_TITLES = new Set(['other', 'floorplan', 'logo']);

/**
 * Hard exclusions — never claim these as interior page heroes.
 */
const UNDESIRABLE_KEYWORDS = [
  'bathroom',
  'bath room',
  'master bath',
  'guest bath',
  'full bath',
  'half bath',
  'restroom',
  'toilet',
  'parking lot',
  'parking garage',
  'parking space',
  'carport',
  'dumpster',
  'trash',
  'garbage',
  'recycling bin',
  'mechanical',
  'utility closet',
  'hvac',
  'site plan',
  'floor plan',
  'floorplan',
  'logo',
  'map pin',
  'qr code',
];

/** Soft penalties — prefer something else when available. */
const LOW_VALUE_KEYWORDS = [
  'parking',
  'garage',
  'laundry',
  'washer',
  'dryer',
  'closet',
  'hallway',
  'corridor',
  'stair',
  'elevator',
  'mailbox',
  'office desk',
  'empty room',
];

/**
 * Standout quality signals — used as a baseline so weak page matches still
 * land on a strong photo rather than a random gallery index.
 */
const STANDOUT_KEYWORDS = [
  'staged',
  'staging',
  'furnished',
  'living room',
  'living',
  'great room',
  'kitchen',
  'dining',
  'bedroom',
  'exterior',
  'building',
  'entrance',
  'entry',
  'lobby',
  'courtyard',
  'pool',
  'clubhouse',
  'lounge',
  'fitness',
  'gym',
  'aerial',
  'drone',
  'skyline',
  'patio',
  'balcony',
  'amenity',
  'community',
];

/**
 * Primary interior pages — claim unique gallery photos first, in this order.
 * Earlier pages win when two pages want the same standout shot.
 * Keys match Preferred InnerPageHero bgGroupPath prefixes (e.g. amenitieshero).
 */
const PRIMARY_PAGE_ORDER = [
  'floorplanshero',
  'amenitieshero',
  'neighborhoodhero',
  'galleryhero',
  'virtualtourhero',
  'contacthero',
  'scheduleatourhero',
  'petpolicyhero',
  'interactivemaphero',
  'reviewshero',
  'applynowhero',
  'residentaccesshero',
  'faqhero',
  'preferredemployerhero',
  'sister-properties-hero',
] as const;

/**
 * Utility / legal pages — use leftovers only; otherwise keep the featured
 * image so primary pages can exhaust the good gallery shots uniquely.
 */
const FEATURED_FALLBACK_PAGES = new Set([
  'privacypolicyhero',
  'sitemaphero',
  'accessibilityhero',
  'thankyouhero',
  'fairhousinghero',
]);

const FEATURED_FALLBACK_ORDER = [
  'thankyouhero',
  'privacypolicyhero',
  'sitemaphero',
  'accessibilityhero',
  'fairhousinghero',
] as const;

/**
 * Page-specific preference keywords keyed by InnerPageHero page hint.
 */
const PAGE_KEYWORDS: Record<string, string[]> = {
  floorplanshero: [
    'staged',
    'staging',
    'furnished',
    'apartment',
    'unit',
    'interior',
    'living room',
    'living',
    'kitchen',
    'bedroom',
    'dining',
    'great room',
    'open concept',
  ],
  amenitieshero: [
    'pool',
    'fitness',
    'gym',
    'clubhouse',
    'lounge',
    'amenity',
    'amenities',
    'courtyard',
    'grill',
    'bbq',
    'spa',
    'rooftop',
    'recreation',
    'community',
  ],
  petpolicyhero: [
    'pet',
    'dog',
    'dog park',
    'pet spa',
    'pet wash',
    'dog wash',
    'animal',
  ],
  galleryhero: [
    'exterior',
    'building',
    'staged',
    'living',
    'pool',
    'community',
    'aerial',
  ],
  neighborhoodhero: [
    'exterior',
    'aerial',
    'drone',
    'skyline',
    'neighborhood',
    'street',
    'building',
    'entrance',
    'location',
    'surrounding',
  ],
  virtualtourhero: [
    'staged',
    'staging',
    'furnished',
    'apartment',
    'interior',
    'living',
    'kitchen',
    'bedroom',
    'tour',
  ],
  interactivemaphero: [
    'aerial',
    'drone',
    'exterior',
    'building',
    'map',
    'community',
    'entrance',
  ],
  contacthero: [
    'exterior',
    'entrance',
    'entry',
    'lobby',
    'leasing',
    'office',
    'building',
    'community',
  ],
  scheduleatourhero: [
    'exterior',
    'entrance',
    'lobby',
    'building',
    'community',
    'courtyard',
    'staged',
  ],
  residentaccesshero: [
    'exterior',
    'entrance',
    'lobby',
    'building',
    'community',
  ],
  applynowhero: [
    'exterior',
    'building',
    'staged',
    'living',
    'community',
    'entrance',
  ],
  reviewshero: [
    'exterior',
    'community',
    'pool',
    'courtyard',
    'lounge',
    'building',
  ],
  faqhero: [
    'exterior',
    'community',
    'building',
    'entrance',
  ],
  preferredemployerhero: [
    'exterior',
    'building',
    'community',
    'entrance',
    'lounge',
  ],
  'sister-properties-hero': [
    'exterior',
    'building',
    'community',
    'aerial',
  ],
};

function getUrlPath(url: string | null | undefined): string {
  if (!url) return '';

  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return '';
  }
}

/** Normalize CDN variants so the same asset isn't assigned to two pages. */
function photoIdentity(photo: GalleryPhoto): string {
  const url = photo.image || photo.original_url || '';
  if (!url) return '';

  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function photoText(photo: GalleryPhoto): string {
  const parts = [
    photo.title,
    photo.description,
    photo.alt_text,
    getUrlPath(photo.image),
    getUrlPath(photo.original_url),
    photo.source_page_urls,
  ];

  return parts
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

function hasKeyword(text: string, keyword: string): boolean {
  return text.includes(keyword);
}

function scoreKeywords(text: string, keywords: string[], weight = 1): number {
  return keywords.reduce(
    (score, keyword) => score + (hasKeyword(text, keyword) ? weight : 0),
    0,
  );
}

function isExcludedCategory(photo: GalleryPhoto): boolean {
  const title = (photo.title || '').toLowerCase().trim();
  return EXCLUDED_CATEGORY_TITLES.has(title);
}

function hasPhotoUrl(photo: GalleryPhoto): boolean {
  return Boolean(photo.image || photo.original_url);
}

function pageKeywordsFor(preset: string): string[] {
  return PAGE_KEYWORDS[preset] || STANDOUT_KEYWORDS;
}

/**
 * Score a gallery photo for use as an interior-page hero.
 * Higher is better. Undesirable photos return a large negative score.
 */
export function scoreHeroPhoto(
  photo: GalleryPhoto,
  pageKeywords: string[],
): number {
  if (!hasPhotoUrl(photo) || isExcludedCategory(photo)) return -1000;

  const text = photoText(photo);
  if (!text.trim()) {
    // Untitled photos can still be fine — treat as neutral standout candidate.
    return 0;
  }

  if (UNDESIRABLE_KEYWORDS.some((keyword) => hasKeyword(text, keyword))) {
    return -500;
  }

  const pageScore = scoreKeywords(text, pageKeywords, 3);
  const standoutScore = scoreKeywords(text, STANDOUT_KEYWORDS, 1);
  const lowValuePenalty = scoreKeywords(text, LOW_VALUE_KEYWORDS, 2);

  const category = (photo.title || '').toLowerCase().trim();
  let categoryBoost = 0;
  if (
    category === 'apartment' &&
    pageKeywords.some(
      (k) => k.includes('apartment') || k.includes('living') || k.includes('kitchen'),
    )
  ) {
    categoryBoost = 2;
  } else if (
    category === 'community' &&
    pageKeywords.some(
      (k) => k.includes('community') || k.includes('pool') || k.includes('exterior'),
    )
  ) {
    categoryBoost = 2;
  }

  return pageScore + standoutScore + categoryBoost - lowValuePenalty;
}

function pickBestUnused(
  gallery: GalleryPhoto[],
  pageKey: string,
  usedIndices: Set<number>,
  usedUrls: Set<string>,
  minScore: number,
): number {
  const keywords = pageKeywordsFor(pageKey);
  let bestIndex = -1;
  let bestScore = minScore - 1;

  for (let index = 0; index < gallery.length; index += 1) {
    if (usedIndices.has(index)) continue;

    const identity = photoIdentity(gallery[index]);
    if (identity && usedUrls.has(identity)) continue;

    const score = scoreHeroPhoto(gallery[index], keywords);
    if (score < minScore) continue;
    if (score <= bestScore) continue;

    bestScore = score;
    bestIndex = index;
  }

  return bestIndex;
}

function claimPhoto(
  gallery: GalleryPhoto[],
  index: number,
  usedIndices: Set<number>,
  usedUrls: Set<string>,
): void {
  usedIndices.add(index);
  const identity = photoIdentity(gallery[index]);
  if (identity) usedUrls.add(identity);
}

/**
 * Deterministically assign a unique gallery index (or -1 = featured image)
 * to every known interior hero page. Safe to call per-page: the result for
 * a given gallery is always the same.
 */
export function assignHeroImages(
  gallery: GalleryPhoto[],
): Map<string, number> {
  const assignments = new Map<string, number>();
  if (!gallery.length) return assignments;

  const usedIndices = new Set<number>();
  const usedUrls = new Set<string>();

  for (const pageKey of PRIMARY_PAGE_ORDER) {
    // Accept neutral untitled photos (score 0) so sparse galleries still
    // get unique heroes before falling back to featured.
    const index = pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 0);
    assignments.set(pageKey, index);
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  // Leftovers only — otherwise these pages keep the featured image.
  for (const pageKey of FEATURED_FALLBACK_ORDER) {
    const index = pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 1);
    assignments.set(pageKey, index);
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  return assignments;
}

/**
 * Pick a gallery index for an interior page hero.
 *
 * Returns a unique index across pages when possible. Returns `-1` to signal
 * "use the property featured image" (utility pages, or when no unused
 * desirable photo remains).
 *
 * @param gallery Property photo gallery
 * @param pageKey InnerPageHero page hint (e.g. amenitieshero)
 */
export function pickHeroImage(
  gallery: GalleryPhoto[],
  pageKey: string,
): number {
  if (!gallery.length || !pageKey) return -1;

  const assignments = assignHeroImages(gallery);

  if (assignments.has(pageKey)) {
    return assignments.get(pageKey)!;
  }

  // Unknown page: claim any remaining unused standout, else featured.
  if (FEATURED_FALLBACK_PAGES.has(pageKey)) {
    return -1;
  }

  const usedIndices = new Set<number>();
  const usedUrls = new Set<string>();
  for (const index of assignments.values()) {
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  return pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 0);
}
